import { Routes, RouterModule } from '@angular/router';
import { NgModule } from '@angular/core';
import { VoiceMessageDownloadComponent } from '@nimbus-global/features/shared/components/voice-message-download/voice-message-download.component';
import { VoiceMessageTranscriptDownloadComponent } from '@nimbus-global/features/shared/components/voice-message-transcript-download/voice-message-transcript-download.component';
import { LandingPageComponent } from '@nimbus-portal/common/components/landing-page/landing-page.component';
import { AuthGuard } from '@nimbus-library/features/authentication/guards/auth.guard';
import { CheckFeatureFlippingInFeatureManagementGuard } from '@nimbus-global/features/shared/services/routing/check-feature-flipping-in-feature-management.guard';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { CiCheckCustomerOverviewGuard } from '@nimbus-portal/features/customer-insights/services/ci-check-customer-overview.guard';
import { CheckPermissionsGuard } from '@nimbus-global/features/shared/services/routing/check-permissions.guard';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { createGlobalPermissionsModelGuard } from '@nimbus-global/features/shared/services/routing/global-permissions-model.guard';
import { companionSplitScreenExpandGuard } from '@nimbus-library/features/companion-chat/companion-split-screen-expand.guard';

const checkCompanionConversationPortalGuard = createGlobalPermissionsModelGuard(
  (model) => model?.tenant?.allowCompanionConversationForPortal ?? false,
);

const appRoutes: Routes = [
  {
    path: 'provisioning',
    loadChildren: () => import('../features/provisioning/provisioning.module').then((m) => m.ProvisioningModule),
    data: { loadRole: false, hideMenu: true },
  },
  {
    path: 'live-view',
    loadChildren: () => import('../features/reporting/reporting.module').then((m) => m.ReportingModule),
    canActivate: [AuthGuard],
    data: { trackingModule: 'Live View', useTeamContext: true },
  },
  {
    path: 'statistics',
    loadChildren: () => import('../features/team-reporting/team-reporting.module').then((m) => m.TeamReportingModule),
    canActivate: [AuthGuard],
    data: { trackingModule: 'Statistics', useTeamContext: true },
  },
  {
    path: 'team-member-details',
    loadChildren: () => import('../features/team-member-details/team-member-details.module').then((m) => m.TeamMemberDetailsModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'workflow',
    loadChildren: () => import('../features/workflow/workflow.module').then((m) => m.WorkflowModule),
    canActivate: [AuthGuard],
    data: { useTeamContext: true },
  },
  {
    path: 'personal/my-sessions',
    loadChildren: () => import('@nimbus-portal/features/my-sessions/my-sessions.module').then((m) => m.MySessionsModule),
    canActivate: [AuthGuard],
    data: { trackingModule: 'My Sessions' },
  },
  {
    path: 'personal/crm',
    redirectTo: '/personal/my-sessions',
    pathMatch: 'full',
  },
  {
    path: 'dashboards',
    loadChildren: () => import('@nimbus-portal/features/dashboard/dashboard.module').then((m) => m.NimbusDashboardModule),
    canActivate: [AuthGuard],
    data: { trackingModule: 'Dashboards' },
  },
  {
    path: 'personal',
    loadChildren: () => import('../features/personal/personal.module').then((m) => m.PersonalModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'authentication',
    loadChildren: () =>
      import('../features/authentication-process/authentication-process.module').then((m) => m.AuthenticationProcessModule),
  },
  {
    path: 'sessions-list',
    loadChildren: () => import('../features/historical-sessions/team-sessions.module').then((m) => m.TeamSessionsModule),
    canActivate: [AuthGuard],
    data: { trackingModule: 'Session List', useTeamContext: true },
  },
  {
    path: 'settings',
    loadChildren: () => import('@nimbus-portal/features/settings/settings.module').then((m) => m.SettingsModule),
    canActivate: [AuthGuard, CheckPermissionsGuard],
    data: {
      trackingModule: 'Service Settings',
      useTeamContext: true,
      permissions: [NimbusPermission.ServicesRead],
      fallbackUrl: ['/'],
    },
  },
  {
    path: 'manage',
    loadChildren: () => import('../features/manage/manage.module').then((m) => m.ManageModule),
    data: { baseUrl: 'manage', trackingModule: 'Configuration' },
    canActivate: [AuthGuard],
  },
  {
    path: 'personal/voicemails',
    loadChildren: () => import('../features/voicemails/voicemails.module').then((m) => m.VoicemailsModule),
    canActivate: [AuthGuard, CheckFeatureFlippingInFeatureManagementGuard, CheckPermissionsGuard],
    data: {
      trackingModule: 'Voicemails',
      useTeamContext: true,
      features: [Feature.ManagerVoicemails, Feature.VoicemailContactCenter],
      permissions: [NimbusPermission.MedialibraryListCallRecords],
      fallbackUrl: ['/'],
    },
  },
  {
    path: 'task-flow-visualizer',
    loadChildren: () => import('../features/task-flow-visualizer/task-flow-visualizer.module').then((m) => m.TaskFlowVisualizerModule),
    canActivate: [AuthGuard, CheckFeatureFlippingInFeatureManagementGuard],
    data: {
      trackingModule: 'Task Flow Visualizer',
      useTeamContext: true,
      features: [Feature.TaskFlowVisualizer],
      fallbackUrl: ['/'],
    },
  },
  {
    path: 'personal/attendantconsole',
    loadChildren: () => import('../features/attendant-console/attendant-console.module').then((m) => m.AttendantConsoleModule),
    canActivate: [AuthGuard],
    data: { trackingModule: 'Attendant Console' },
  },
  {
    path: 'customer-overview/customer-insights',
    loadChildren: () => import('../features/customer-insights/customer-insights.module').then((m) => m.CustomerInsightsModule),
    canActivate: [AuthGuard, CheckFeatureFlippingInFeatureManagementGuard, CiCheckCustomerOverviewGuard, CheckPermissionsGuard],
    data: {
      feature: Feature.CustomerJourney,
      permissions: [NimbusPermission.CustomerOverviewRead],
      trackingModule: 'Customer Insights',
    },
  },
  {
    path: 'location',
    loadChildren: () => import('../../../../global/src/features/geo-location/geo-location.module').then((m) => m.GeoLocationModule),
    canActivate: [AuthGuard],
    data: { loadRole: false, hideMenu: true },
  },
  {
    path: 'tools',
    loadChildren: () => import('../features/tools/tools.module').then((m) => m.ToolsModule),
  },
  {
    path: 'wrong-location',
    loadChildren: () => import('../features/wrong-location-page/wrong-location-page.module').then((m) => m.WrongLocationPageModule),
  },
  {
    path: 'voice-message/download',
    component: VoiceMessageDownloadComponent,
    canActivate: [AuthGuard],
    data: { loadContext: false, loadRole: false, hideMenu: true },
  },
  {
    path: 'voice-message/transcript',
    component: VoiceMessageTranscriptDownloadComponent,
    canActivate: [AuthGuard],
    data: { loadContext: false, loadRole: false, hideMenu: true },
  },
  {
    path: 'companion-chat',
    canActivate: [
      companionSplitScreenExpandGuard,
      AuthGuard,
      CheckFeatureFlippingInFeatureManagementGuard,
      checkCompanionConversationPortalGuard,
    ],
    loadChildren: () => import('../../../../shared/src/features/companion-chat/companion-chat.routes').then((m) => m.companionChatRoutes),
    data: { features: [Feature.CompanionConversationPortal], fallbackUrl: ['/'], trackingModule: 'CompanionChatPortal' },
  },
  {
    path: '**',
    component: LandingPageComponent,
    canActivate: [AuthGuard],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes, { enableTracing: false })],
  exports: [RouterModule],
  declarations: [],
  providers: [],
})
export class RoutingModule {}
